import { EditCategoryWidget } from '@modules/common/components/EditCategoryWidget';
import { Category } from '@modules/common/types';
import { AddPhrase } from '@modules/feedback/components/AddPredictions/AddPhrase';
import { AddPredictionForm } from '@modules/feedback/components/AddPredictions/AddPredictionForm';
import { useFeedbackOnFeedbackStore } from '@modules/feedback/store';
import { FeedbackOnFeedbackActionType } from '@modules/feedback/store/feedbackOnFeedbackActionType';
import { trackTaxonomyEvent } from '@modules/taxonomy/helpers/trackTaxonomyEvents';
import { useUpdateTrackedKeyword } from '@modules/taxonomy/hooks';
import { Stack, Typography } from '@mui/material';
import { ManipulationType } from 'generated/graphql';
import { FormEvent, useState } from 'react';
import { FeedbackLabelType } from 'v2/core/entities/feedback';
import { ReasonOrEntityLabelType } from 'v2/core/entities/feedback/labels';
import { UUID } from 'v2/core/entities/uuid';
import { useGetEntityLabel } from 'v2/views/layouts/utils';

interface AddTrackedKeywordProps {
  feedbackID: UUID;
  option: ReasonOrEntityLabelType;
  onCancel: () => void;
  onAfterAddingTrackedKeyword: (prediction?: ReasonOrEntityLabelType) => void;
}

export const AddTrackedKeyword = ({
  feedbackID,
  option,
  onCancel,
  onAfterAddingTrackedKeyword,
}: AddTrackedKeywordProps) => {
  const [phraseList, setPhraseList] = useState<string[]>([]);
  const dispatch = useFeedbackOnFeedbackStore(({ dispatch }) => dispatch);
  const recordInEditMode = useFeedbackOnFeedbackStore(
    ({ recordInEditMode }) => recordInEditMode,
  );
  const [selectedCategory, setSelectedCategory] = useState<Category>(
    Category.Complaint,
  );

  const [, trackedKeywordLabel] = useGetEntityLabel(option.id);

  const updateKeywordInput = {
    id: option.id,
    phrases: Array.from(
      new Set([
        ...(trackedKeywordLabel?.metadata?.phrases ?? []),
        ...phraseList,
      ]),
    ),
  };

  const { updateTrackedKeyword } = useUpdateTrackedKeyword({
    updateKeywordInput,
    onCompleted: () => {
      trackTaxonomyEvent({
        eventName: 'Edit Tracked Keyword Phrases',
        id: updateKeywordInput.id,
        phrases: updateKeywordInput.phrases,
      });
    },
  });

  const onAddTrackedKeyword = (
    event: FormEvent | KeyboardEvent | undefined,
    prediction: ReasonOrEntityLabelType,
  ) => {
    event?.stopPropagation();
    event?.preventDefault();

    if (prediction.type === FeedbackLabelType.Entity) {
      dispatch({
        type: FeedbackOnFeedbackActionType.SET_RECORD_IN_EDIT_MODE,
        payload: {
          id: feedbackID,
          editPredictions: [
            ...(recordInEditMode?.editPredictions ?? []),
            {
              type: FeedbackLabelType.Entity,
              manipulationType: ManipulationType.Add,
              prediction,
              category: selectedCategory,
            },
          ],
        },
      });
      if (phraseList.length > 0) {
        updateTrackedKeyword();
      }
      onAfterAddingTrackedKeyword();
    }
  };

  return (
    <AddPredictionForm
      onCancel={onCancel}
      displayName={option.displayName}
      onSubmit={(event) => onAddTrackedKeyword(event, option)}
    >
      <Stack spacing={0.5}>
        <Typography variant="subtitle1">Select a Category</Typography>
        <EditCategoryWidget
          selectedCategory={selectedCategory}
          updateSelectedCategory={setSelectedCategory}
        />
      </Stack>
      <AddPhrase
        feedbackID={feedbackID}
        labelID={option.id}
        phraseList={phraseList}
        setPhraseList={setPhraseList}
      />
    </AddPredictionForm>
  );
};
